import React from 'react';
import { FaVolumeUp, FaSpinner } from 'react-icons/fa';
import ReportButton from './reports/ReportButton';
import MaskedBlock from './reports/MaskedBlock';

interface MobileChatBubbleProps {
  message: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    audio_url?: string;
    created_at: string;
  };
  user: {
    id: string;
    email: string;
    profile_image?: string | null;
  } | null;
  isPlaying: boolean;
  playingMessageId: string | null;
  onPlayAudio: (messageId: string, audioUrl: string) => void;
  generatingAudio: boolean;
  safetyState?: {
    isSafe: boolean;
    flaggedCategories: string[];
    isBlocked: boolean;
  };
  safetyChecked?: boolean;
  onReportModalToggle?: (isOpen: boolean) => void;
}

const MobileChatBubble: React.FC<MobileChatBubbleProps> = ({
  message,
  user,
  isPlaying,
  playingMessageId,
  onPlayAudio,
  generatingAudio,
  safetyState = { isSafe: true, flaggedCategories: [], isBlocked: false },
  safetyChecked = true,
  onReportModalToggle
}) => {
  const isUser = message.role === 'user';
  const isCurrentlyPlaying = playingMessageId === message.id && isPlaying;

  // AI Teacher profile image
  const aiAvatarUrl = "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/Public/image_681cd52d835cd.jpg";

  // Format AI response to remove markdown and improve readability
  const formatAIResponse = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
      .replace(/\*(.*?)\*/g, '$1') // Remove italic markdown
      .replace(/#{1,6}\s/g, '') // Remove heading markdown
      .replace(/`(.*?)`/g, '$1') // Remove code markdown
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove link markdown
      .trim();
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 px-2`}>
      <div className={`flex items-start space-x-2 w-full max-w-[85%] ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
        {/* Avatar - Smaller on mobile */}
        <div className="flex-shrink-0">
          <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-gray-800">
            {isUser ? (
              user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt="User"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-yellow-600 text-black font-bold text-xs">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              )
            ) : (
              <img 
                src={aiAvatarUrl}
                alt="AI Teacher"
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        {/* Message Content - Mobile optimized */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} min-w-0 flex-1`}>
          {/* Message Bubble - Responsive sizing */}
          {!isUser && (!safetyState.isSafe || safetyState.isBlocked) && safetyChecked ? (
            <MaskedBlock 
              reason={safetyState.isBlocked ? 'Contenu bloqué par modération' : 'Contenu potentiellement inapproprié'}
              isBlocked={safetyState.isBlocked}
              className="max-w-full"
            >
              <div className="relative p-2.5 rounded-xl max-w-full bg-white/10 backdrop-blur-lg text-white border border-white/10 rounded-bl-sm">
                <div className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere word-break-break-word">
                  {formatAIResponse(message.content)}
                </div>
              </div>
            </MaskedBlock>
          ) : (
            <div className={`relative p-2.5 rounded-xl max-w-full ${
              isUser 
                ? 'bg-yellow-500 text-black rounded-br-sm' 
                : 'bg-white/10 backdrop-blur-lg text-white border border-white/10 rounded-bl-sm'
            }`}>
              {/* Message Text - Mobile optimized */}
              <div className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere word-break-break-word">
                {isUser ? message.content : formatAIResponse(message.content)}
              </div>
            </div>
          )}

          {/* Audio Controls and Report Button for AI messages */}
          {!isUser && (
            <div className="mt-2 flex items-center justify-end w-full space-x-2">
              {/* Audio Controls */}
              {message.audio_url && (
                <button
                  onClick={() => onPlayAudio(message.id, message.audio_url!)}
                  disabled={generatingAudio}
                  className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 text-xs order-1"
                >
                  {generatingAudio ? (
                    <FaSpinner className="w-3 h-3 animate-spin" />
                  ) : (
                    <FaVolumeUp className={`w-3 h-3 ${isCurrentlyPlaying ? 'text-yellow-400' : ''}`} />
                  )}
                  <span className={`${isCurrentlyPlaying ? 'text-yellow-400' : ''} truncate`}>
                    {generatingAudio ? 'Generating...' : isCurrentlyPlaying ? 'Playing...' : 'Listen'}
                  </span>
                </button>
              )}
              
              {/* Report Button */}
              <ReportButton
                contentId={message.id}
                contentPreview={message.content}
                variant="B"
                className="flex-shrink-0 order-2"
                onModalToggle={onReportModalToggle}
              />
            </div>
          )}

          {/* Timestamp - Smaller on mobile */}
          <div className={`mt-1 text-xs text-white/50 ${isUser ? 'text-right' : 'text-left'}`}>
            {new Date(message.created_at).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileChatBubble;